import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import IosCard from '../../components/iOSCard';
import { db } from '../../firebase'; 
import { 
    collection, 
    query, 
    onSnapshot, 
    doc, 
    updateDoc, 
    orderBy, 
    where,
    deleteDoc 
} from 'firebase/firestore';
import { 
    UsersIcon, 
    UserPlusIcon, 
    MagnifyingGlassIcon, 
    EnvelopeIcon, 
    PowerIcon,
    CheckCircleIcon,
    TrashIcon,
    PencilSquareIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';

const StudentManager = () => {
    const navigate = useNavigate();
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    
    // التعديلات الجديدة:
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [editingStudent, setEditingStudent] = useState(null);
    const [phoneData, setPhoneData] = useState({ studentPhone: "", parentPhone: "" });
    
    const [stats, setStats] = useState({
        total: 0,
        active: 0,
        today: 0
    });

    const showToast = (msg) => {
        setToastMessage(msg);
        setTimeout(() => setToastMessage(""), 3000);
    };

    const sendWhatsAppAlert = (parentPhone, studentName) => {
        const message = `تنبيه من منصة نور أكاديمي: عزيزي ولي أمر الطالب ${studentName}، نود إعلامكم بأن الطالب حالياً مشتت التركيز عن المحاضرة. ⚠️`;
        const url = `https://wa.me/${parentPhone}?text=${encodeURIComponent(message)}`;
        window.open(url, '_blank');
    };

    const handleSavePhones = async () => {
        if (!editingStudent) return;
        const studentRef = doc(db, "users", editingStudent.id);
        
        setIsModalOpen(false);
        await updateDoc(studentRef, {
            studentPhone: phoneData.studentPhone,
            parentPhone: phoneData.parentPhone
        });
        
        showToast("تم تحديث أرقام التواصل يا ضلعي! 📞");
    };

    // 🔄 [STUDENT_DATA_SYNC]: جلب بيانات الطلاب ومراقبة نشاطهم
    useEffect(() => {
        const q = query(collection(db, "users"), where("role", "==", "student"), orderBy("createdAt", "desc"));
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const studentsData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setStudents(studentsData);
            
            const activeCount = studentsData.filter(s => s.status !== 'blocked').length;
            
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const todayCount = studentsData.filter(s => {
                const createDate = s.createdAt?.toDate ? s.createdAt.toDate() : new Date(s.createdAt);
                return createDate >= today;
            }).length;

            setStats({
                total: studentsData.length,
                active: activeCount,
                today: todayCount
            });
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const filteredStudents = students.filter(student => 
        student.name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
        student.email?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const toggleStudentStatus = async (id, currentStatus) => {
        try {
            const studentRef = doc(db, "users", id);
            await updateDoc(studentRef, {
                status: currentStatus === 'blocked' ? 'active' : 'blocked'
            });
        } catch (error) {
            console.error("Error updating status:", error);
            showToast("فشل تحديث الحالة 🛑");
        }
    };

    const startChat = (studentName) => {
        navigate(`/chat?name=${studentName}`);
    };

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center bg-transparent">
            <div className="w-10 h-10 border-4 border-[#007AFF] border-t-transparent rounded-full animate-spin"></div>
        </div>
    );

    return (
        <div style={{ fontFamily: "'Cairo', sans-serif" }} className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700" dir="rtl">
            
            {/* 🎯 [CUSTOM TOAST: MESSAGE] */}
            <AnimatePresence>
                {toastMessage && (
                    <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[20000] flex items-center justify-center p-4">
                        <div className="ios-glass px-8 py-4 rounded-[30px] border border-white/50 shadow-2xl bg-white/95 text-[#007AFF] font-black text-center">
                            {toastMessage}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
            
            {/* ⬇️ [MODAL: PHONE_UPDATE] */}
            <AnimatePresence>
                {isModalOpen && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
                        <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} className="ios-glass max-w-sm w-full p-8 rounded-[40px] border border-white/40 shadow-2xl bg-white/95">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="font-black text-xl text-gray-800">تحديث أرقام التواصل 📞</h3>
                                <button onClick={() => setIsModalOpen(false)}><XMarkIcon className="w-6 h-6 text-gray-500"/></button>
                            </div>
                            <div className="space-y-4">
                                <input type="text" placeholder="رقم موبايل الطالب" value={phoneData.studentPhone} onChange={(e) => setPhoneData({...phoneData, studentPhone: e.target.value})} className="w-full p-4 rounded-2xl bg-gray-100 outline-none font-bold" />
                                <input type="text" placeholder="رقم ولي الأمر" value={phoneData.parentPhone} onChange={(e) => setPhoneData({...phoneData, parentPhone: e.target.value})} className="w-full p-4 rounded-2xl bg-gray-100 outline-none font-bold" />
                                <button onClick={handleSavePhones} className="w-full py-4 bg-[#007AFF] text-white rounded-2xl font-black active:scale-95 transition-all">حفظ التغييرات</button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <IosCard title="إدارة حسابات الطلاب" icon={<UsersIcon className="w-6 h-6" />} color="blue" className="w-full">
                <div className="relative mb-6">
                    <MagnifyingGlassIcon className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#007AFF] opacity-70" />
                    <input 
                        type="text" 
                        placeholder="ابحث عن طالب بالاسم أو البريد..." 
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full p-4 pr-12 rounded-2xl bg-white/50 border border-white/20 outline-none focus:ring-2 focus:ring-[#007AFF]/20 transition-all font-black text-gray-700 shadow-inner"
                    />
                </div>

                <div className="custom-scroll overflow-x-auto rounded-[28px] border border-white/30 bg-white/20 shadow-sm">
                    <table className="w-full text-right border-collapse min-w-[950px]">
                        <thead>
                            <tr className="bg-[#007AFF]/5 text-[#007AFF] text-xs">
                                <th className="p-5 font-black uppercase">بيانات الطالب</th>
                                <th className="p-5 font-black uppercase">الاشتراك</th>
                                <th className="p-5 font-black uppercase">حالة الحساب</th>
                                <th className="p-5 font-black uppercase">بيانات التواصل</th>
                                <th className="p-5 font-black uppercase">حالة التركيز</th>
                                <th className="p-5 font-black uppercase text-center">إجراءات التحكم</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/10">
                            {filteredStudents.length > 0 ? (
                                filteredStudents.map((student) => (
                                    <tr key={student.id} className={`transition-all duration-300 hover:bg-white/40 ${student.status === 'blocked' ? 'bg-red-50/20' : ''}`}>
                                        <td className="p-5">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-11 h-11 rounded-2xl flex items-center justify-center text-white font-black shadow-md ${student.status === 'blocked' ? 'bg-gray-400' : 'bg-[#007AFF]'}`}>
                                                    {student.name ? student.name[0] : '?'}
                                                </div>
                                                <div>
                                                    <p className="font-black text-gray-800 text-sm">{student.name || "بدون اسم"}</p>
                                                    <p className="text-[10px] font-bold text-gray-400">{student.email}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-5 text-sm font-black text-[#007AFF]">
                                            {student.isActivated ? (student.activeCourse || "مشترك") : "بانتظار التفعيل"}
                                        </td>
                                        <td className="p-5">
                                            <span className={`px-4 py-1.5 rounded-full text-[9px] font-black tracking-widest uppercase ${!student.isActivated ? 'bg-gray-100 text-gray-600' : student.status !== 'blocked' ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'}`}>
                                                {!student.isActivated ? 'غير مفعل' : (student.status !== 'blocked' ? 'Active' : 'Blocked')}
                                            </span>
                                        </td>
                                        <td className="p-5">
                                            <div className="flex items-center gap-2">
                                                <div className="text-[9px] font-bold text-gray-600">
                                                    <div>{student.studentPhone || "---"}</div>
                                                    <div>{student.parentPhone || "---"}</div>
                                                </div>
                                                <button onClick={() => { setEditingStudent(student); setPhoneData({ studentPhone: student.studentPhone || "", parentPhone: student.parentPhone || "" }); setIsModalOpen(true); }} className="p-1.5 bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-600 hover:text-white transition-all shadow-sm">
                                                    <PencilSquareIcon className="w-4 h-4"/>
                                                </button>
                                            </div>
                                        </td>
                                        <td className="p-5">
                                            {/* هنا شرط إضافي: نعتمد على 'lastSeen' أو تحديث الحالة في كود الطالب */}
                                            {student.status === 'active' && student.isFocused !== undefined ? (
                                                student.isFocused === false ? (
                                                    <button onClick={() => sendWhatsAppAlert(student.parentPhone, student.name)} className="flex items-center gap-1 bg-red-100 text-red-600 px-3 py-1 rounded-full animate-pulse">
                                                        <span className="text-[9px] font-black">تنبيه ولي الأمر 📱</span>
                                                    </button>
                                                ) : (
                                                    <span className="text-[10px] font-black text-emerald-500">مركز ✓</span>
                                                )
                                            ) : (
                                                <span className="text-[10px] font-bold text-gray-300">غير متصل</span>
                                            )}
                                        </td>
                                        <td className="p-5 text-center">
                                            <div className="flex items-center justify-center gap-2">
                                                {!student.isActivated && (
                                                    <button onClick={async () => { if(window.confirm("حذف الطالب؟")) await deleteDoc(doc(db, "users", student.id)); }} className="p-3 bg-red-50 text-red-600 rounded-2xl hover:bg-red-600 hover:text-white transition-all shadow-sm">
                                                        <TrashIcon className="w-5 h-5" />
                                                    </button>
                                                )}
                                                <button onClick={() => startChat(student.name)} className="p-3 bg-pink-50 text-pink-600 rounded-2xl hover:bg-pink-600 hover:text-white transition-all shadow-sm">
                                                    <EnvelopeIcon className="w-5 h-5" />
                                                </button>
                                                <button onClick={() => toggleStudentStatus(student.id, student.status)} className={`px-5 py-2.5 rounded-2xl transition-all shadow-md font-black text-[10px] ${student.status !== 'blocked' ? 'bg-red-500 text-white' : 'bg-emerald-500 text-white'}`}>
                                                    <PowerIcon className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="p-10 text-center font-black text-gray-400">لا يوجد طلاب مسجلين حالياً 🔍</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="mt-8 flex justify-between items-center px-4">
                    <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">المجموع المفلتر: {filteredStudents.length} طالب</p>
                </div>
            </IosCard>
        </div>
    );
};

export default StudentManager;